import type { CSSProperties, FC, PropsWithChildren, ReactElement, ReactNode, TransitionEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useModifiers } from '../../../styles/hooks/useModifiers';
import { debounce } from '../../../utils/debounce';
import { BackButton } from '../../controls/backButton/BackButton';
import { IconButton, Icons } from '../../controls/iconButton/IconButton';

import type { ClickCallback } from './types';

import styles from './navDrawer.module.css';

const DEFAULT_KEYBOARD_HEIGHT = 320; // Approximate keyboard height for mobile devices
const OPERATORS_EXTRA_SPACE = 60; // Space adjustment for operator alignment (iframes)

export interface NavDrawerProps extends PropsWithChildren {
  open: boolean;
  handleClose: ClickCallback<HTMLElement>;
  handleExpand: (expanded: boolean) => void;
  isExpanded: boolean;
  isForcedExpanded: boolean;
  title: ReactNode;
  handleBack?: ClickCallback<HTMLButtonElement>;
  backButtonVisible?: boolean;
  onTransitionEnd?: (event: TransitionEvent<HTMLDivElement>) => void;
}

export const NavDrawer: FC<NavDrawerProps> = ({
  children,
  handleBack,
  handleClose,
  handleExpand,
  open,
  backButtonVisible,
  title,
  isExpanded,
  isForcedExpanded,
  onTransitionEnd,
}): ReactElement => {
  const [delta, setDelta] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const deltaRef = useRef<number | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const handleWrapperRef = useRef<HTMLDivElement>(null);
  const hasMovedRef = useRef(false);
  const originRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  let roughKeyboardHeight = DEFAULT_KEYBOARD_HEIGHT;
  const operatorExtraSpace = OPERATORS_EXTRA_SPACE;

  useEffect(() => {
    if (open) {
      setDelta(null);
    }
  }, [open]);

  useEffect(() => {
    const handleWrapperElement = handleWrapperRef.current;
    if (!handleWrapperElement) return;
    if (isForcedExpanded) return;

    const handlePointerDown = (event: PointerEvent): void => {
      originRef.current = event.clientY;
      setDelta(0);

      deltaRef.current = 0;
      setIsDragging(true);

      document.addEventListener('pointermove', handleMove, { passive: true });
      document.addEventListener('pointerup', handlePointerUp, { passive: true });
    };

    const handleMove = (event: PointerEvent): void => {
      if (originRef.current === null) return;
      const newDelta = originRef.current - event.clientY;
      if (isExpanded && newDelta > 0) {
        setDelta(0);
      } else if (!isExpanded && newDelta < 0) {
        setDelta(newDelta);
      } else {
        setDelta(newDelta);
      }
      deltaRef.current = newDelta;
      hasMovedRef.current = true;
    };

    const handlePointerUp = (): void => {
      const deltaValue = deltaRef.current;
      if (deltaValue !== null) {
        if ((isExpanded && deltaValue < -30) || (!isExpanded && deltaValue > 30)) {
          handleExpand(deltaValue > 0);
          setDelta(null);
        } else if (!isExpanded && deltaValue < -30) {
          handleClose();
        } else if (!hasMovedRef.current || Math.abs(deltaValue) < 4) {
          handleExpand(!isExpanded);
          setDelta(null);
        } else {
          setDelta(null);
        }
      }

      setIsDragging(false);

      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handlePointerUp);

      originRef.current = null;
      deltaRef.current = null;
      hasMovedRef.current = false;
    };

    const handleTouchStart = (event: TouchEvent): void => {
      event.stopPropagation();
      event.preventDefault();
    };

    handleWrapperElement.addEventListener('touchstart', handleTouchStart);
    handleWrapperElement.addEventListener('pointerdown', handlePointerDown);

    return (): void => {
      handleWrapperElement.removeEventListener('touchstart', handleTouchStart);
      handleWrapperElement.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isExpanded, isForcedExpanded, handleBack, handleExpand, handleClose]);

  useEffect(() => {
    const handleFocusIn = debounce((event: FocusEvent): void => {
      if (window.matchMedia('(orientation: portrait)').matches) {
        const target = event.target as HTMLInputElement | HTMLTextAreaElement | undefined;
        if (target && ['text', 'textarea'].includes(target.type)) {
          if (window.top === window.self) {
            roughKeyboardHeight = window.visualViewport?.height || window.innerHeight || 360;
          }
          setMaxHeight(roughKeyboardHeight - operatorExtraSpace);
        }
      }
    }, 300);

    const handleFocusOut = debounce((): void => {
      setMaxHeight(null);
    }, 200);

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);

    return (): void => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const mods = useModifiers(
    styles,
    'navDrawer',
    {
      expanded: isExpanded,
      forcedExpanded: isForcedExpanded,
      isDragging,
      open,
    },
    { withBaseClass: true }
  );
  const wrapperMods = useModifiers(styles, 'navDrawer__wrapper', { open }, { withBaseClass: true });
  const curtainMods = useModifiers(styles, 'navDrawer__curtain', { open }, { withBaseClass: true });

  return (
    <>
      <div className={curtainMods} onClick={handleClose} />
      <div
        className={wrapperMods}
        ref={wrapperRef}
        style={{ '--wrapperMaxHeight': maxHeight ? `${maxHeight}px` : undefined } as CSSProperties}
        onTransitionEnd={(e): void => {
          if (e.target === wrapperRef.current) onTransitionEnd?.(e);
        }}
      >
        <div
          data-testid="nav-drawer"
          className={mods}
          style={{ '--delta': `${delta ?? 0}px` } as CSSProperties}
          ref={drawerRef}
        >
          <div data-testid="nav-drawer-header" className={styles.navDrawer__header}>
            <div className={styles.navDrawer__headerHandleWrapper} ref={handleWrapperRef}>
              <div data-testid="nav-drawer-handle" className={styles.navDrawer__headerHandle} />
            </div>
            <BackButton onClick={handleBack} visible={backButtonVisible} />
            <div data-testid="nav-drawer-header-title" className={styles.navDrawer__headerTitle}>
              {title}
            </div>
            <IconButton data-testid="nav-drawer-close-button" ghost icon={Icons.Close} onClick={handleClose} tiny />
          </div>
          <div data-testid="nav-drawer-container" className={styles.navDrawer__container}>
            {children}
          </div>
          <div data-testid="nav-drawer-footer" className={styles.navDrawer__footer} />
        </div>
      </div>
    </>
  );
};
